import { memo } from 'react';
import { Checkbox, Label } from '@flote/ui';
import { ClipboardCheck } from 'lucide-react';
import type { CompletionRequirementsState } from '../hooks';

interface CompletionRequirementsProps {
  requirements: CompletionRequirementsState;
  onRequirementChange: (field: keyof CompletionRequirementsState, value: boolean) => void;
  disabled?: boolean;
  idPrefix?: string;
  title?: string;
  description?: string;
  signatureLabel?: string;
  photoLabel?: string;
  notesLabel?: string;
  layout?: 'row' | 'column';
}

export const CompletionRequirements = memo<CompletionRequirementsProps>(({
  requirements,
  onRequirementChange,
  disabled = false,
  idPrefix = '',
  title = 'Completion Requirements',
  description = 'Driver must complete these requirements before marking the task as delivered',
  signatureLabel = 'Signature',
  photoLabel = 'Photo',
  notesLabel = 'Notes',
  layout = 'column',
}) => {
  const layoutClasses = layout === 'row'
    ? 'flex flex-wrap gap-4'
    : 'space-y-3';

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-medium">
        <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
        {title}
        <span className="text-xs font-normal text-muted-foreground">(optional)</span>
      </div>

      <div className={layoutClasses}>
        <div className="flex items-center space-x-2">
          <Checkbox
            id={`${idPrefix}requireSignature`}
            checked={requirements.requireSignature}
            onCheckedChange={(checked) => onRequirementChange('requireSignature', checked === true)}
            disabled={disabled}
          />
          <Label
            htmlFor={`${idPrefix}requireSignature`}
            className="text-sm font-normal cursor-pointer"
          >
            {signatureLabel}
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id={`${idPrefix}requirePhoto`}
            checked={requirements.requirePhoto}
            onCheckedChange={(checked) => onRequirementChange('requirePhoto', checked === true)}
            disabled={disabled}
          />
          <Label
            htmlFor={`${idPrefix}requirePhoto`}
            className="text-sm font-normal cursor-pointer"
          >
            {photoLabel}
          </Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id={`${idPrefix}requireNotes`}
            checked={requirements.requireNotes}
            onCheckedChange={(checked) => onRequirementChange('requireNotes', checked === true)}
            disabled={disabled}
          />
          <Label
            htmlFor={`${idPrefix}requireNotes`}
            className="text-sm font-normal cursor-pointer"
          >
            {notesLabel}
          </Label>
        </div>
      </div>

      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  );
});

CompletionRequirements.displayName = 'CompletionRequirements';
